/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import { DashboadPageNavOption } from '../../../../util/enums';
import { isTrailerRenter } from '../../../../util/genericHelpers';
import { isHaulStarMember, isPausedMember } from '../../../../util/subscriptionHelpers';
import { createBillingPortalSessionAPI } from '../../../../util/api';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    hasUnreadMessages,
    customLinks,
    onLogout,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const [portalLoading, setPortalLoading] = useState(false);
  const isMember = isHaulStarMember(currentUser);
  // Paused members are still members — keep the "Manage membership" entry so they
  // can reach the page to resume.
  const hasMembership = isMember || isPausedMember(currentUser);
  // Confirmed renters don't get owner-only surfaces (post listing, listings,
  // dashboard, Haul Star). Owners + untagged users are treated as owners.
  const isRenter = isTrailerRenter(currentUser);

  const handleManageBilling = async e => {
    if (e && e.preventDefault) e.preventDefault();
    if (portalLoading) return;
    setPortalLoading(true);
    try {
      const res = await createBillingPortalSessionAPI();
      const url = res && (res.url || res.data?.url);
      if (url) {
        window.location.assign(url);
      } else {
        setPortalLoading(false);
      }
    } catch (err) {
      setPortalLoading(false);
      if (typeof window !== 'undefined') {
        window.location.assign('/subscription');
      }
    }
  };

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <div className={css.customLinksWrapper}>{extraLinks}</div>

          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('WorkshopPage'))}
            name="WorkshopPage"
          >
            The Workshop
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ToolsIndexPage'))}
            name="ToolsIndexPage"
          >
            <FormattedMessage id="TopbarDesktop.tools" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('BlogPage'))}
            name="BlogPage"
          >
            <FormattedMessage id="TopbarDesktop.blog" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('FAQPage'))}
            name="FAQPage"
          >
            <FormattedMessage id="TopbarDesktop.faqLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('SeoCategoriesPage'))}
            name="SeoCategoriesPage"
          >
            <FormattedMessage id="TopbarDesktop.categories" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ContactUsPage'))}
            name="ContactUsPage"
          >
            <FormattedMessage id="TopbarDesktop.contact" />
          </NamedLink>
          <div className={css.spacer} />
        </div>
        <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  // For the inquiry process the transition-based count is ~0, so show a simple
  // unread dot when there are unread inbox messages and no count badge.
  const inboxDot =
    !notificationCountBadge && hasUnreadMessages ? (
      <div className={css.notificationDot} />
    ) : null;

  const displayName = user.attributes.profile.firstName;

  const inboxTab = currentUserHasListings ? 'sales' : 'orders';

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <div className={css.accountLinksWrapper}>
          {/* {currentUserHasListings ? (
            <NamedLink
              className={classNames(css.inbox, currentPageClass('DashboardPage'))}
              name="DashboardPage"
              params={{ tab: DashboadPageNavOption.TRANSACTIONS_DETAILS }}
            >
              <FormattedMessage id="TopbarDesktop.dashboard" />
            </NamedLink>
          ) : null} */}
          <NamedLink
            className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
            {inboxDot}
          </NamedLink>
          {isRenter ? null : (
            <NamedLink
              className={classNames(css.inbox, currentPageClass(`UserDashboardPage`))}
              name="UserDashboardPage"
            >
              <FormattedMessage id="TopbarMobileMenu.userDashboardLink" />
              {notificationCountBadge}
            </NamedLink>
          )}
          {isRenter ? null : (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
            </NamedLink>
          )}
          {isRenter ? null : (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('ToolboxIndexPage'))}
              name="ToolboxIndexPage"
            >
              <FormattedMessage id="TopbarMobileMenu.toolboxLink" />
            </NamedLink>
          )}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>

          {isRenter ? null : hasMembership ? (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('MembershipPage'))}
              name="MembershipPage"
            >
              <FormattedMessage id="TopbarMobileMenu.manageMembershipLink" />
            </NamedLink>
          ) : (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('SubscriptionPage'))}
              name="SubscriptionPage"
            >
              <FormattedMessage id="TopbarMobileMenu.getHaulStarLink" />
            </NamedLink>
          )}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('WorkshopPage'))}
            name="WorkshopPage"
          >
            The Workshop
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ToolsIndexPage'))}
            name="ToolsIndexPage"
          >
            <FormattedMessage id="TopbarDesktop.tools" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('BlogPage'))}
            name="BlogPage"
          >
            <FormattedMessage id="TopbarMobileMenu.blogPage" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ContactUsPage'))}
            name="ContactUsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.contact" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('FAQPage'))}
            name="FAQPage"
          >
            <FormattedMessage id="TopbarDesktop.faqLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('SeoCategoriesPage'))}
            name="SeoCategoriesPage"
          >
            <FormattedMessage id="TopbarDesktop.categories" />
          </NamedLink>
        </div>
        <div className={css.customLinksWrapper}>{extraLinks}</div>
        <div className={css.spacer} />
      </div>
      {isRenter ? null : (
        <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div>
      )}
    </div>
  );
};

TopbarMobileMenu.defaultProps = {
  currentUser: null,
  notificationCount: 0,
  hasUnreadMessages: false,
  currentPage: null,
};

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  hasUnreadMessages: bool,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
